import { ProductKeys } from '@/types/ProductEnums';
import { DOT_SYMBOL } from '~/constants';
import { capitalizeFirstLetter } from '~/utils';
import { isProductInStock, isProductDiscontinued } from '~/utils/ProductAvailability';
import type { ProductModels, NormalizedProductVariantModels } from '~/types/models/product.models';
import { useLocalization } from '~/composables/_next/useLocalization';
import { getCapacityString } from '~/helpers/convertCapacityUnit';

export default () => {
  const { locale } = useLocalization();
  const sortAlphabetically = (a, b) => {
    const aColor = a.attributes.color?.label;
    const bColor = b.attributes.color?.label;

    if (aColor > bColor) return 1;
    if (aColor < bColor) return -1;
    return 0;
  };

  const getVariantAttribute = (attributes, variant, attribute, isLocalized = false, skipCurrent = false) => {
    if (isLocalized) return attributes?.value || variant.value?.[attribute]?.value || undefined;

    return attributes?.[attribute] || (skipCurrent ? undefined : variant.value?.[attribute]) || undefined;
  };

  const checkAttribute = (existingOptions, currentOption, attribute, isLocalized = false) => {
    // ".value? breaks?"
    if (isLocalized) return existingOptions?.[attribute]?.value === currentOption?.[attribute];
    return existingOptions?.[attribute] === currentOption?.[attribute];
  };

  const findRawAttribute = (variant, attributeName) => {
    return variant.attributesRaw.find((attribute) => attribute.name === attributeName)?.value;
  };

  const normalizeVariants = (variants: ProductModels.CustomProductVariant[]): NormalizedProductVariantModels.Root[] => {
    return variants
      .map((variant) => {
        if (!variant?.sku) return null;

        const capacity = findRawAttribute(variant, ProductKeys.capacity);

        const capacityUnit = findRawAttribute(variant, ProductKeys.capacityUnit);

        const capacityType = findRawAttribute(variant, ProductKeys.capacityType);

        const color = findRawAttribute(variant, ProductKeys.color);

        const capacityAndUnitLabel = getCapacityString(capacity, capacityUnit?.label?.[locale]);

        const capacityTypeLabel = capacityType?.label?.[locale] || '';

        const separator = capacityTypeLabel ? ` ${DOT_SYMBOL} ` : '';

        const isSellable = findRawAttribute(variant, ProductKeys.isSellable);

        const isComplimentary = findRawAttribute(variant, ProductKeys.isComplimentary);

        const entry: NormalizedProductVariantModels.Root = {
          id: variant.id,
          sku: variant.sku,
          price: variant.price,
          isSellable: isSellable?.key === ProductKeys.inStock,
          isDiscontinued: isSellable.key === ProductKeys.discontinued,
          isComplimentary,
          mainImage: variant?.images?.[0]?.url,
          images: variant?.images,
          isMaster: variant._master,
          name: variant._name,
          attributesRaw: variant.attributesRaw,
          attributes: {
            capacity: {
              capacity,
              value: capacity,
              [ProductKeys.capacityType]: capacityType?.key,
              [ProductKeys.capacityUnit]: capacityUnit?.key,
              label: capacityAndUnitLabel + separator + capitalizeFirstLetter(capacityTypeLabel),
            },
            color: {
              label: color?.label[locale],
              value: color?.key,
              color: color?.key,
            },
          },
        };
        return entry;
      })
      .filter((v) => !!v);
  };

  const findFirstSellable = (variants) => {
    const masterVariant = variants.find((variant) => variant.isMaster === true) ?? {};
    const inStockVariants = variants.filter((variant) => isProductInStock(variant)) ?? [];

    if (
      (masterVariant && isProductInStock(masterVariant) && !isProductDiscontinued(masterVariant)) ||
      !inStockVariants.length
    ) {
      return masterVariant;
    } else {
      return inStockVariants[0];
    }
  };

  // todo find better way
  const checkNormalizedAttribute = (variant, attributes, key) => {
    return variant.attributes[key]?.label === attributes[key]?.label;
  };

  const findVariantByAttrib = (variants, attributes) => {
    let matchedVariant = variants.find(
      (variant) =>
        checkNormalizedAttribute(variant, attributes, ProductKeys.capacity) &&
        checkNormalizedAttribute(variant, attributes, ProductKeys.color),
    );

    if (!matchedVariant || !matchedVariant.isSellable) {
      const sellableVariants = variants.filter((v) => checkNormalizedAttribute(v, attributes, ProductKeys.capacity));

      const bestMatch = sellableVariants.filter((v) => checkNormalizedAttribute(v, attributes, ProductKeys.color));

      const sortedSellableVariants = sellableVariants.sort(sortAlphabetically);
      matchedVariant = bestMatch.length > 0 ? bestMatch[0] : sortedSellableVariants[0];
    }

    return matchedVariant;
  };

  return {
    getVariantAttribute,
    checkAttribute,
    findRawAttribute,
    sortAlphabetically,
    normalizeVariants,
    findFirstSellable,
    findVariantByAttrib,
  };
};
